import { useMemo, useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { FaChevronLeft, FaChevronRight, FaDownload, FaRegFileExcel } from 'react-icons/fa';
import { formatDate01 } from '../../helper/helper';
import './CalendarComponent.css';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
  'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des',
];

/**
 * Pilih bulan (Jan–Des) di atas, kalender hari tetap di bawah.
 */
const AttendanceCalendar = ({
  dataAbsensi = [],
  hariLibur = [],
  activeStartDate,
  onActiveStartDateChange,
  onClickDay,
  onExportExcel,
  exporting = false,
  className = 'mx-auto w-full md:min-w-full md:max-w-md rounded-lg',
}) => {
  const initial = activeStartDate || new Date();
  const [year, setYear] = useState(initial.getFullYear());
  const [monthDate, setMonthDate] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (!activeStartDate) return;
    setYear(activeStartDate.getFullYear());
    setMonthDate(new Date(activeStartDate.getFullYear(), activeStartDate.getMonth(), 1));
  }, [activeStartDate]);

  const attendanceByDate = useMemo(() => {
    const map = new Map();
    (dataAbsensi || []).forEach((item) => {
      const key = formatDate01(item.tanggal);
      if (!map.has(key)) map.set(key, item);
    });
    return map;
  }, [dataAbsensi]);

  const holidayDates = useMemo(() => {
    const set = new Set();
    (hariLibur || []).forEach((hl) => set.add(formatDate01(hl.tanggal)));
    return set;
  }, [hariLibur]);

  const selectMonth = (monthIndex) => {
    const start = new Date(year, monthIndex, 1);
    setMonthDate(start);
    setSelectedDate(null);
    onActiveStartDateChange?.({ activeStartDate: start });
  };

  const changeYear = (nextYear) => {
    setYear(nextYear);
    const start = new Date(nextYear, monthDate.getMonth(), 1);
    setMonthDate(start);
    setSelectedDate(null);
    onActiveStartDateChange?.({ activeStartDate: start });
  };

  const statusMeta = (item, dateStr, date) => {
    if (item) {
      if (item.waktuKeluar && item.waktuMasuk) {
        return { markerClass: 'bg-green-600', text: 'Hadir', tile: 'att-hadir' };
      }
      if (item.status === 'izin') {
        return { markerClass: 'bg-blue-500', text: 'Izin', tile: 'att-izin' };
      }
      if (item.status === 'cuti') {
        return { markerClass: 'bg-purple-500', text: 'Cuti', tile: 'att-cuti' };
      }
      if (item.waktuMasuk) {
        return { markerClass: 'bg-yellow-600', text: 'Masuk', tile: 'att-masuk' };
      }
    }
    if (holidayDates.has(dateStr) || date.getDay() === 0) {
      return { markerClass: 'bg-red-400', text: 'Libur', tile: 'att-libur' };
    }
    return null;
  };

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const dateStr = formatDate01(date);
    const meta = statusMeta(attendanceByDate.get(dateStr), dateStr, date);
    return (
      <div className="att-label-slot">
        {meta ? (
          <div className={`att-label flex justify-center items-center ${meta.markerClass}`}>
            <span className="text-[6px] md:text-[8px] text-white font-semibold leading-none">{meta.text}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return null;

    const dateStr = formatDate01(date);
    const item = attendanceByDate.get(dateStr);
    const classes = [];

    if (holidayDates.has(dateStr)) classes.push('holiday');
    if (date.getDay() === 0) classes.push('sunday');

    const meta = statusMeta(item, dateStr, date);
    if (meta?.tile) classes.push(meta.tile);

    return classes.length ? classes.join(' ') : null;
  };

  const activeMonth = monthDate.getMonth();

  return (
    <>
      {/* Jan–Des di atas kalender */}
      <div className="mb-3 rounded-xl border border-gray-200 bg-white overflow-hidden" style={{ filter: 'drop-shadow(0px 0px 6px rgb(210, 210, 210))' }}>
        <div className="flex items-center justify-between bg-[#1B3C33] text-white px-3 py-2">
          <button type="button" className="p-1.5 rounded-full hover:bg-white/15" onClick={() => changeYear(year - 1)} aria-label="Tahun sebelumnya">
            <FaChevronLeft className="text-sm" />
          </button>
          <span className="font-semibold text-sm">{year}</span>
          <button type="button" className="p-1.5 rounded-full hover:bg-white/15" onClick={() => changeYear(year + 1)} aria-label="Tahun berikutnya">
            <FaChevronRight className="text-sm" />
          </button>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 p-2">
          {MONTH_NAMES.map((name, index) => {
            const isActive = activeMonth === index && monthDate.getFullYear() === year;
            return (
              <button
                key={name}
                type="button"
                onClick={() => selectMonth(index)}
                className={`rounded-md px-1 py-2 text-xs font-medium transition-all active:scale-[0.97]
                  ${isActive
                    ? 'bg-[#1B3C33] text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-[#1B3C33]/10 hover:text-[#1B3C33]'
                  }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Kalender hari */}
      <div className="rounded-lg overflow-hidden" style={{ filter: 'drop-shadow(0px 0px 8px rgb(189, 189, 189))' }}>
        <div className="flex items-center justify-between bg-[#1B3C33] text-white px-4 py-3">
          <span className="font-semibold text-sm capitalize">
            {monthDate.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
          </span>
          {onExportExcel && (
            <button
              type="button"
              onClick={onExportExcel}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-transparent border-2 border-white hover:bg-green-800 disabled:bg-gray-400 text-white text-[11px] font-semibold"
              title="Download Excel"
            >
              {/* <FaRegFileExcel className="text-xs" /> */}
              <span className='flex items-center gap-1'> <FaDownload /> {exporting ? '...' : 'Download'}</span>
            </button>
          )}
        </div>
        <Calendar
          onClickDay={(date) => {
            setSelectedDate(date);
            onClickDay?.(date);
          }}
          tileContent={tileContent}
          className={`${className} attendance-day-calendar`}
          locale='id-ID'
          calendarType="iso8601"
          showNeighboringMonth={true}
          maxDetail="month"
          minDetail="month"
          prevLabel={null}
          nextLabel={null}
          prev2Label={null}
          next2Label={null}
          tileClassName={tileClassName}
          activeStartDate={monthDate}
          onActiveStartDateChange={({ activeStartDate: start }) => {
            if (!start) return;
            setMonthDate(start);
            setYear(start.getFullYear());
            onActiveStartDateChange?.({ activeStartDate: start });
          }}
          value={selectedDate}
        />
      </div>
      <div className="mt-3 flex flex-wrap gap-3 justify-center">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-green-600"></div>
          <span className="text-[10px] text-gray-700">Hadir</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
          <span className="text-[10px] text-gray-700">Izin</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div>
          <span className="text-[10px] text-gray-700">Cuti</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-600"></div>
          <span className="text-[10px] text-gray-700">Masuk</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
          <span className="text-[10px] text-gray-700">Libur</span>
        </div>
      </div>
    </>
  );
};

export default AttendanceCalendar;
